/* eslint-disable @typescript-eslint/no-this-alias */
import * as Sentry from '@sentry/browser';

import {
  DEBUG_MODE,
  getUrlResults,
  getUrlSaveResult,
  runLocalTest,
} from '../spdConfig';

import { CodeAnswer, CoreFieldName } from './answer';
import { Loader, LoaderError } from './loader';
import { ModeSurvey } from './survey';
import * as Configuration from './testConfiguration';

type TaskNumber = string;

export type Value = string | number | string[] | undefined; //udzielona odpowiedź;

/*type Reaction = {
  latencyTime: number; //czas do pojawienia bodźca
  reactionTime: number; // czas od pojawienia się bodźca do udzielenia odpowiedzi
  value: Value;
  codeAnswer: CodeAnswer;
  coreFieldName: CoreFieldName | null;
  modeSurvey: ModeSurvey;
};*/

export enum CriticalType {
  DriverNeurologist = 'driver_neurologist',
  DriverDiabetologist = 'driver_diabetologist',
  DriverPsychiatrist = 'driver_psychiatrist',
  DriverOphthalmologist = 'driver_ophthalmologist',
  Neurologist = 'neurologist',
  Diabetologist = 'diabetologist',
  Psychiatrist = 'psychiatrist',
  Laryngologist = 'laryngologist',
  Ophthalmologist = 'ophthalmologist',
  Blockade = 'blockade',
  Warning = 'warning',
}

export enum ValueType {
  String = 'string',
  StringList = 'stringList',
  File = 'file',
  PNG = 'png',
  Webm = 'webm',
  Stetho = 'stetho',
  Audiometry = 'audiometry',
  AudiometryList = 'audiometryList',
  Spiro = 'spiro',
  HarmfulFactorList = 'harmfulFactorList',
  RawECG = 'rawECG',
  ResultsECG = 'resultsECG',
}

export type GivenAnswer = {
  value: Value;
  valueOriginal: Value | null;
  lange: Configuration.Lange;
  codeAnswer: CodeAnswer;
  criticalType?: CriticalType | null;
  valueType?: ValueType | null;
};

type ComplexReaction = {
  latencyTime: number; //czas do pojawienia bodźca od aktywacji pola spoczynkowego
  decisionTime: number; //czas podjęcia decyzji od aktywacji pola spoczynkowego (np. oderwanie palca)
  reactionTime: number; // czas od pojawienia się bodźca lub aktywacji pola spoczynkowego do udzielenia odpowiedzi lub czas udzielenia odpowiedzi od startu zadania
  answers: GivenAnswer[];
  coreFieldName: CoreFieldName | null;
  modeSurvey: ModeSurvey;
};

export type Result = {
  taskNumber: TaskNumber;
  taskText: string;
  taskTime: number; // całkowity czas prezentacji zadania zlicza wszystkie podejścia??? suma czasów
  reactions: ComplexReaction[];
};

type Memory = {
  surveyId: string;
  testId: string;
  results: Result[];
};

export type IndicatorTestResult = {
  id: string;
  name: string;
  mode: string;
  result: number;
  normName: string;
  recalculatedResult: number;
  interpretedResult: string;
};

const memory: Memory[] = [];

function saveInMemory(
  surveyId: string,
  testId: string,
  taskNumber: TaskNumber,
  result: Result,
) {
  let find = false;
  for (const mem of memory) {
    if (mem.surveyId === surveyId && mem.testId === testId) {
      find = true;
      let update = false;
      for (let i = 0; i < mem.results.length; i++) {
        const res = mem.results[i];
        if (res.taskNumber === taskNumber) {
          mem.results[i] = result;
          update = true;
        }
      }
      if (!update) {
        mem.results.push(result);
      }
    }
  }
  if (!find) {
    memory.push({ surveyId: surveyId, testId: testId, results: [result] });
  }
}

async function loadFromMemory(
  surveyId: string,
  testId: string,
): Promise<Result[]> {
  for (const mem of memory) {
    if (mem.surveyId === surveyId && mem.testId === testId) {
      return mem.results;
    }
  }
  return [];
}
export class Results {
  protected dirtyResultsForTask: Map<TaskNumber, boolean>;
  protected activeSaveResults: Promise<void>[] = [];

  constructor(
    protected surveyId: string,
    protected testId: string,
    protected results: Result[] = [],
  ) {
    this.dirtyResultsForTask = new Map<TaskNumber, boolean>();
  }

  protected getResultForTask(taskNumber: TaskNumber): Result | null {
    for (const res of this.results) {
      if (res.taskNumber === taskNumber) {
        return res;
      }
    }
    return null;
  }

  protected buildPayload(taskNumber: TaskNumber) {
    const result = this.getResultForTask(taskNumber);
    if (!result) {
      return null;
    }
    const timeStamp = new Date().toISOString();
    // snapshot to avoid mutation while saving
    const resultSnapshot = JSON.parse(JSON.stringify(result)) as Result;
    return { timestamp: timeStamp, result: resultSnapshot };
  }
  /*
  public getLastReactionForTask(taskNumber: TaskNumber): Reaction | null {
    const result = this.getResultForTask(taskNumber);
    return result ? result.reactions[result.reactions.length - 1] : null;
  }*/

  public getLastComplexReactionForTask(
    taskNumber: TaskNumber,
  ): ComplexReaction | null {
    const result = this.getResultForTask(taskNumber);
    return result && result.reactions
      ? result.reactions[result.reactions.length - 1]
      : null;
  }

  /*
  public getLastCodeAnswerForTask(taskNumber: TaskNumber): CodeAnswer | null {
    const reaction = this.getLastReactionForTask(taskNumber);
    return reaction ? reaction.codeAnswer : null;
  }*/

  public getValueFromGiven(
    given: GivenAnswer | null,
    withTranslation = true,
  ): Value | null {
    if (given) {
      if (withTranslation) {
        return given.value;
      } else {
        return given.valueOriginal || given.value;
      }
    }
    return null;
  }

  public getLastGivenValueForTask(
    taskNumber: TaskNumber,
    withTranslation = true,
  ): Value | null {
    const given = this.getLastGivenAnswerForTask(taskNumber);
    return this.getValueFromGiven(given, withTranslation);
  }

  public getLastGivenAnswerForTask(taskNumber: TaskNumber): GivenAnswer | null {
    const given = this.getLastGivenAnswersForTask(taskNumber);
    return given ? given[given.length - 1] : null;
  }

  public getLastGivenAnswersForTask(
    taskNumber: TaskNumber,
  ): GivenAnswer[] | null {
    const complexReaction = this.getLastComplexReactionForTask(taskNumber);
    return complexReaction ? complexReaction.answers : null;
  }

  protected pushOrRemoveGivenAnswer(
    complexReactions: ComplexReaction[],
    givenAnswer: GivenAnswer,
  ) {
    const answers = complexReactions[complexReactions.length - 1].answers;
    let alreadyPush = false;
    for (let i = 0; i < answers.length; i++) {
      if (answers[i].codeAnswer === givenAnswer.codeAnswer) {
        answers.splice(i, 1);
        alreadyPush = true;
      }
    }
    if (!alreadyPush) {
      answers.push(givenAnswer);
    }
  }

  // Funkcja pomocnicza do obliczania rozmiaru kolekcji results
  private calculateReactionsSize(reactions: ComplexReaction[]): number {
    return new Blob([JSON.stringify(reactions)]).size;
  }

  protected reduceReactionsSize(reactions: ComplexReaction[]) {
    // Ograniczenie do maksymalnie 20 historycznych odpowiedzi
    if (reactions.length > 20) {
      reactions = reactions.slice(-20);
    }

    // Sprawdzanie rozmiaru by nie przekroczył 5 MB, przy musi być zapamiętana przynajmniej 1 odpowiedź
    if (
      this.calculateReactionsSize(reactions) > 5 * 1024 * 1024 &&
      reactions.length > 2
    ) {
      // 5 MB
      reactions.shift(); // Usuwa najstarszy element
    }
    return reactions;
  }

  // eslint-disable-next-line complexity
  public collectAnswer(
    complexAnswer: boolean,
    taskNumber: TaskNumber,
    taskText: string,
    taskLange: Configuration.Lange,
    valueOriginal: Value | null | undefined,
    answerCode: CodeAnswer,
    taskTime: number,
    latencyTime: number,
    decisionTime: number,
    reactionTime: number,
    value: Value | undefined,
    coreFieldName: CoreFieldName | null | undefined,
    criticalType: CriticalType | null | undefined,
    valueType: ValueType | null | undefined,
    mode: ModeSurvey,
  ) {
    // zapisuje odpowiedź jako złożoną lub jako kolejną
    DEBUG_MODE &&
      console.log(
        'collectAnswer',
        complexAnswer,
        taskNumber,
        taskText,
        taskLange,
        valueOriginal,
        answerCode,
        taskTime,
        latencyTime,
        decisionTime,
        reactionTime,
        value,
        coreFieldName,
        criticalType,
        value ? valueType : null,
        mode,
      );
    if (answerCode) {
      const givenAnswer: GivenAnswer = {
        value: value,
        valueOriginal: valueOriginal,
        lange: taskLange,
        codeAnswer: answerCode,
        criticalType: criticalType || null,
        valueType: value ? valueType || null : null,
      };
      const reaction: ComplexReaction = {
        reactionTime: reactionTime, //do zastanowienia jak ma być podawany czas reakcji dla multi wyboru - każde zaznaczenie to oddzielna odpowiedź więc sytuacja rozwiązana ;-)
        latencyTime: latencyTime,
        decisionTime: decisionTime,
        answers: [givenAnswer],
        coreFieldName: coreFieldName || null,
        modeSurvey: mode,
      };
      this.dirtyResultsForTask.set(taskNumber, true); /// oznaczam, ze rezultat należy zapisać - do optymalizacji zapisów i rozjazdu danych wracających z serwera
      const result = this.getResultForTask(taskNumber);
      if (result) {
        result.taskTime = result.taskTime + taskTime; //reaction.reactionTime;
        if (complexAnswer && result.reactions) {
          reaction.answers = [
            ...result.reactions[result.reactions.length - 1].answers,
          ]; //klonuję ostatnie odbiowie. Nowa reakcja będzie złożeniem ostatnich i dopisaniem nowego wyboru lub wykreśleniem już wybranej pozycji
          result.reactions.push(reaction);
          this.pushOrRemoveGivenAnswer(result.reactions, givenAnswer);
        } else {
          result.reactions?.push(reaction);
        }
        result.reactions = this.reduceReactionsSize(result.reactions);
      } else {
        const newRes: Result = {
          taskNumber: taskNumber,
          taskText: taskText,
          taskTime: taskTime, ///reaction.reactionTime + reaction.latencyTime,
          reactions: [reaction],
        };
        this.results.push(newRes);
      }
    }
  }

  /*
  public saveResults() {
    // przesyłam wszystkie odpowiedzi łącznie z ostatnią
    const timeStamp = new Date().toISOString();
    DEBUG_MODE &&
      console.log(
        'Zapis wyników dla badania:',
        this.surveyId,
        ' testu:',
        this.testId,
        'timeStamp',
        timeStamp,
        'WYNIK:',
        this.results,
      );
  }
*/
  protected blockingErrorSeries = 0;
  public useblockingErrorSeries() {
    this.blockingErrorSeries++;
  }

  // eslint-disable-next-line complexity
  protected async attemptSaveResult(taskNumber: TaskNumber) {
    if (!this.dirtyResultsForTask.get(taskNumber)) {
      return;
    }
    const blockingErrorSeries = this.blockingErrorSeries;

    const payloadToSend = this.buildPayload(taskNumber);
    if (!payloadToSend) {
      return;
    }
    // clear dirty flag once we have a promise responsible for saving
    // any new answer during save will set dirty again and queue another save
    this.dirtyResultsForTask.delete(taskNumber);

    DEBUG_MODE &&
      console.log(
        '-> SAVE RESULT',
        runLocalTest() ? 'in MEMORY for survey:' : 'in CORE for survey:',
        this.surveyId,
        'test:',
        this.testId,
        'timeStamp:',
        payloadToSend.timestamp,
        'RESULT:',
        payloadToSend.result,
      );

    let postPromise: Promise<void> | null = null;
    try {
      if (runLocalTest()) {
        saveInMemory(
          this.surveyId,
          this.testId,
          taskNumber,
          payloadToSend.result,
        );
        return;
      }

      const onError = (error: unknown) => {
        if (this.blockingErrorSeries !== blockingErrorSeries) {
          return;
        }
        // informuj tylko raz na serię, potem zwiększ serię blokującą
        this.useblockingErrorSeries();
        const loaderError = error as LoaderError;
        Loader.ErrorsStack.add(loaderError);
      };

      postPromise = Loader.postSyncWithRetry(
        getUrlSaveResult() + this.surveyId + '/' + this.testId,
        payloadToSend,
        true,
        false,
        onError,
      );

      this.activeSaveResults.push(postPromise);

      await postPromise;

      DEBUG_MODE &&
        console.log(
          'Core received the result for task:',
          taskNumber,
          'execution:',
          blockingErrorSeries,
        );
    } catch (e) {
      console.error('CATCH exception saveResult', e, this.blockingErrorSeries);
      Sentry.captureException(e);
    } finally {
      if (postPromise) {
        this.activeSaveResults = this.activeSaveResults.filter(
          req => req !== postPromise,
        );
      }
    }
  }

  public async saveResult(taskNumber: TaskNumber) {
    await this.attemptSaveResult(taskNumber);
  }

  public removeUnsavedResults() {
    this.dirtyResultsForTask.forEach((isDirty, taskNumber) => {
      if (isDirty) {
        // Usuń wszystkie rekordy z results, które mają ten sam taskNumber
        this.results = this.results.filter(
          result => result.taskNumber !== taskNumber,
        );
      }
    });

    // Można wyczyścić mapę dirtyResultsForTask, nie powinno być żadnych brudnych wyników
    this.dirtyResultsForTask.clear();
  }

  public async waitForAllSaveResults(): Promise<void> {
    const blockingErrorSeries = this.blockingErrorSeries;
    if (this.dirtyResultsForTask.size) {
      /// raczej zbędne ale na wszelki wypadek gdyby jakieś wyniki nie zostały zapisane
      for (const [taskNumber, isDirty] of this.dirtyResultsForTask) {
        if (isDirty) {
          void this.saveResult(taskNumber);
        }
      }
    }
    if (this.activeSaveResults.length > 0) {
      ///czekam, ąż wszystkie promisy z zapisem wyników się wykonają
      await Promise.allSettled(this.activeSaveResults);
    }

    DEBUG_MODE &&
      console.log(
        'Finish waitForAllSaveResults execution:',
        blockingErrorSeries,
      );
  }

  public static async loadResults(surveyId: string, testId: string) {
    const results: Result[] = runLocalTest()
      ? await loadFromMemory(surveyId, testId)
      : await Loader.getSyncJSONWithVersion(
          getUrlResults(),
          {
            examId: surveyId,
            testId: testId,
          },
          true,
        );
    DEBUG_MODE && console.log('loadResults', results);
    return new Results(surveyId, testId, results);
  }

  public getResults(): Result[] {
    return this.results;
  }
}
